import { mkShortId } from "@/wab/shared/common";
import {
  FontFaceAsset,
  ProjectDependency,
  Site,
} from "@/wab/shared/model/classes";
import {
  MAX_PRINT_FONT_BYTES,
  MAX_PRINT_FONT_PACKAGE_BYTES,
  PrintFontResource,
  decodePrintFont,
  normalizePrintFonts,
  printFontsCss,
} from "@/wab/shared/publishing/print-fonts";
import {
  InventoryEntry,
  projectPartialInventory,
  validatePersistedMediaRecords,
} from "@/wab/shared/core/media-assets";
import { isEqual } from "lodash";
import { z } from "zod";

export const MAX_FONT_FACE_ASSET_BYTES = MAX_PRINT_FONT_BYTES;
export const MAX_FONT_FACE_PACKAGE_BYTES = MAX_PRINT_FONT_PACKAGE_BYTES;
export const MAX_FONT_FACE_ASSETS = 256;
/** Bound on the whole local + dependency-tree inventory a single read scans:
 * sixteen full packages. Larger scopes are refused, never truncated. */
export const MAX_FONT_FACE_INVENTORY_ASSETS = MAX_FONT_FACE_ASSETS * 16;

/** Owned faces reuse the existing print font codec: canonical base64 payload,
 * signature-verified container format and identical descriptor grammar. */
const descriptorShape = {
  family: z
    .string()
    .trim()
    .min(1)
    .max(200)
    .regex(/^[^<>\\\u0000-\u001f]+$/),
  weight: z
    .string()
    .regex(/^(normal|bold|[1-9][0-9]{0,2}|1000)( ([1-9][0-9]{0,2}|1000))?$/),
  style: z.enum(["normal", "italic", "oblique"]),
  stretch: z
    .string()
    .regex(/^(normal|[1-9][0-9]{0,2}%)( [1-9][0-9]{0,2}%)?$/),
  unicodeRange: z
    .string()
    .min(1)
    .max(10000)
    .regex(
      /^U\+[0-9A-F?]{1,6}(-[0-9A-F]{1,6})?(\s*,\s*U\+[0-9A-F?]{1,6}(-[0-9A-F]{1,6})?)*$/i,
    ),
  format: z.enum(["woff2", "woff", "truetype", "opentype"]),
};
export const fontFaceImportInputSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    ...descriptorShape,
    data: z
      .string()
      .min(8)
      .max(Math.ceil(MAX_FONT_FACE_ASSET_BYTES / 3) * 4)
      .regex(
        /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/,
      ),
    expectedSha256: z
      .string()
      .regex(/^[a-f0-9]{64}$/)
      .optional(),
  })
  .strict();

export type PreparedFontFace = Readonly<{
  name: string;
  family: string;
  weight: string;
  style: "normal" | "italic" | "oblique";
  stretch: string;
  unicodeRange: string;
  format: "woff2" | "woff" | "truetype" | "opentype";
  /** Canonical base64 font bytes (no data: prefix), shared print codec. */
  data: string;
  byteLength: number;
  sha256: string;
}>;

const prepared = new WeakSet<object>();

/** decodePrintFont performs the canonical-base64 and container-signature
 * checks; map its codec errors onto this module's refusal codes. */
function decodeFontBytes(
  resource: Pick<PrintFontResource, "format" | "data">,
): Uint8Array {
  try {
    return decodePrintFont({
      family: "x",
      weight: "normal",
      style: "normal",
      stretch: "normal",
      unicodeRange: "U+0",
      format: resource.format,
      data: resource.data,
      sha256: "0".repeat(64),
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : "";
    throw new Error(
      message.includes("declared format")
        ? "FONT_TYPE_MISMATCH"
        : "FONT_ENCODING_INVALID",
    );
  }
}

/** Canonical bytes only. Signature decoding establishes container format, not
 * glyph coverage or successful shaping; the browser FontFace.load step in the
 * client adapter is the actual parser-acceptance gate. No network effects. */
export async function prepareFontFaceAsset(
  raw: unknown,
  signal?: AbortSignal,
): Promise<PreparedFontFace> {
  const stop = () => {
    if (signal?.aborted) throw new Error("FONT_CANCELED");
  };
  stop();
  const input = fontFaceImportInputSchema.parse(raw);
  const bytes = decodeFontBytes(input);
  stop();
  if (!bytes.byteLength || bytes.byteLength > MAX_FONT_FACE_ASSET_BYTES)
    throw new Error("FONT_TOO_LARGE");
  const hash = new Uint8Array(
    await crypto.subtle.digest("SHA-256", new Uint8Array(bytes)),
  );
  stop();
  const sha256 = Array.from(hash, (b) => b.toString(16).padStart(2, "0")).join(
    "",
  );
  if (input.expectedSha256 && input.expectedSha256 !== sha256)
    throw new Error("FONT_INTEGRITY_MISMATCH");
  const result = Object.freeze({
    name: input.name,
    family: input.family,
    weight: input.weight,
    style: input.style,
    stretch: input.stretch,
    unicodeRange: input.unicodeRange,
    format: input.format,
    data: input.data,
    byteLength: bytes.byteLength,
    sha256,
  });
  prepared.add(result);
  return result;
}

/** Face identity mirrors the print codec: family (case-insensitive) plus the
 * declared axis descriptors and unicode-range, normalized. */
export function fontFaceIdentityKey(
  face: Pick<
    FontFaceAsset,
    "family" | "weight" | "style" | "stretch" | "unicodeRange"
  >,
): string {
  return JSON.stringify([
    face.family.toLowerCase(),
    face.weight,
    face.style,
    face.stretch,
    face.unicodeRange.toUpperCase(),
  ]);
}

/** Must execute within the existing native document recorder/authority guard.
 * Only this module's immutable verified preparation receipts can be committed.
 * A face with an identical identity but different bytes replaces the existing
 * owned source in place (same slot, new uuid); identical bytes dedupe. */
export function commitFontFaceAsset(
  site: Site,
  value: PreparedFontFace,
  expectedLocalAssetIds: string[],
): FontFaceAsset {
  if (!prepared.has(value)) throw new Error("FONT_UNVERIFIED_PREPARATION");
  if (!isEqual(site.fontFaceAssets.map((a) => a.uuid), expectedLocalAssetIds))
    throw new Error("FONT_INVENTORY_CHANGED");
  const key = fontFaceIdentityKey(value);
  const existing = site.fontFaceAssets.find(
    (a) => fontFaceIdentityKey(a) === key,
  );
  if (
    existing &&
    existing.format === value.format &&
    existing.data === value.data &&
    existing.sha256 === value.sha256 &&
    existing.byteLength === value.byteLength &&
    existing.name === value.name
  ) {
    return existing;
  }
  const replacing = !!existing;
  const projectedCount = site.fontFaceAssets.length + (replacing ? 0 : 1);
  const projectedBytes =
    site.fontFaceAssets.reduce((n, a) => n + a.byteLength, 0) +
    value.byteLength -
    (existing?.byteLength ?? 0);
  if (projectedCount > MAX_FONT_FACE_ASSETS || projectedBytes > MAX_FONT_FACE_PACKAGE_BYTES)
    throw new Error("FONT_PACKAGE_TOO_LARGE");
  const asset = new FontFaceAsset({ uuid: mkShortId(), ...value });
  if (existing) {
    site.fontFaceAssets.splice(site.fontFaceAssets.indexOf(existing), 1, asset);
  } else {
    site.fontFaceAssets.push(asset);
  }
  return asset;
}

/** Disposal removes only the owned face record; style references are family
 * name strings, so uncovered variants degrade to the existing external/local
 * font path rather than losing the family. */
export function removeFontFaceAsset(site: Site, uuid: string): FontFaceAsset {
  const index = site.fontFaceAssets.findIndex((a) => a.uuid === uuid);
  if (index < 0) throw new Error("FONT_ASSET_NOT_FOUND");
  const [removed] = site.fontFaceAssets.splice(index, 1);
  return removed;
}

export function cloneFontFaceAsset(asset: FontFaceAsset): FontFaceAsset {
  return new FontFaceAsset({
    uuid: mkShortId(),
    name: asset.name,
    family: asset.family,
    weight: asset.weight,
    style: asset.style,
    stretch: asset.stretch,
    unicodeRange: asset.unicodeRange,
    format: asset.format,
    data: asset.data,
    byteLength: asset.byteLength,
    sha256: asset.sha256,
  });
}

/** Structural shape shared by a live FontFaceAsset and its serialized persisted
 * bundle record, so the same integrity pipeline runs on both. */
export type PersistedFontFaceRecord = Pick<
  FontFaceAsset,
  | "name"
  | "family"
  | "weight"
  | "style"
  | "stretch"
  | "unicodeRange"
  | "format"
  | "data"
  | "byteLength"
  | "sha256"
>;

/**
 * Validates a persisted FontFaceAsset on load: canonical base64, declared
 * descriptor grammar, container signature vs declared format, byte-size
 * bounds, and SHA-256 integrity.
 */
export async function validatePersistedFontFace(
  asset: PersistedFontFaceRecord,
  signal?: AbortSignal,
): Promise<PreparedFontFace> {
  const stop = () => {
    if (signal?.aborted) throw new Error("FONT_CANCELED");
  };
  stop();
  if (asset.byteLength <= 0 || asset.byteLength > MAX_FONT_FACE_ASSET_BYTES) {
    throw new Error("FONT_TOO_LARGE");
  }
  if (asset.data.length > Math.ceil(MAX_FONT_FACE_ASSET_BYTES / 3) * 4) {
    throw new Error("FONT_TOO_LARGE");
  }
  const parsed = fontFaceImportInputSchema
    .omit({ expectedSha256: true })
    .safeParse({
      name: asset.name,
      family: asset.family,
      weight: asset.weight,
      style: asset.style,
      stretch: asset.stretch,
      unicodeRange: asset.unicodeRange,
      format: asset.format,
      data: asset.data,
    });
  if (!parsed.success) throw new Error("FONT_DESCRIPTOR_INVALID");
  const bytes = decodeFontBytes(asset);
  if (bytes.byteLength !== asset.byteLength) throw new Error("FONT_TOO_LARGE");
  stop();
  const hash = new Uint8Array(
    await crypto.subtle.digest("SHA-256", new Uint8Array(bytes)),
  );
  stop();
  const sha256 = Array.from(hash, (b) => b.toString(16).padStart(2, "0")).join(
    "",
  );
  if (asset.sha256 !== sha256) throw new Error("FONT_INTEGRITY_MISMATCH");
  const result = Object.freeze({
    name: asset.name,
    family: asset.family,
    weight: asset.weight,
    style: asset.style,
    stretch: asset.stretch,
    unicodeRange: asset.unicodeRange,
    format: asset.format,
    data: asset.data,
    byteLength: bytes.byteLength,
    sha256,
  });
  prepared.add(result);
  return result;
}

/**
 * Validates all persisted font faces in a Site upon load, enforcing per-face
 * and aggregate bounds (256 faces, print-package byte ceiling). Conflicting
 * identity declarations reuse the print codec's conflict refusal.
 */
export async function validatePersistedFontFaces(
  site: Site,
  signal?: AbortSignal,
): Promise<void> {
  const stop = () => {
    if (signal?.aborted) throw new Error("FONT_CANCELED");
  };
  stop();
  if (site.fontFaceAssets.length > MAX_FONT_FACE_ASSETS) {
    throw new Error("FONT_PACKAGE_TOO_LARGE");
  }
  let totalBytes = 0;
  const identities = new Map<string, PersistedFontFaceRecord>();
  for (const asset of site.fontFaceAssets) {
    stop();
    await validatePersistedFontFace(asset, signal);
    const key = fontFaceIdentityKey(asset);
    const prior = identities.get(key);
    if (
      prior &&
      (prior.sha256 !== asset.sha256 ||
        prior.data !== asset.data ||
        prior.format !== asset.format)
    ) {
      throw new Error("FONT_IDENTITY_CONFLICT");
    }
    identities.set(key, asset);
    totalBytes += asset.byteLength;
    if (totalBytes > MAX_FONT_FACE_PACKAGE_BYTES) {
      throw new Error("FONT_PACKAGE_TOO_LARGE");
    }
  }
}

/**
 * Persisted-load integrity boundary: resolves the Site owned by an unbundled
 * root instance (a raw Site, or a ProjectDependency wrapping one) and enforces
 * persisted font integrity before it can render or codegen. Fail-closed:
 * throws on tampered/invalid bytes. Instances with no owned Site (and legacy
 * sites with an empty/absent fontFaceAssets list) pass through untouched.
 */
export async function validatePersistedFontsInInst(
  inst: unknown,
  signal?: AbortSignal,
): Promise<void> {
  const site = Site.isKnown(inst)
    ? inst
    : ProjectDependency.isKnown(inst)
      ? inst.site
      : undefined;
  if (site?.fontFaceAssets?.length) {
    await validatePersistedFontFaces(site, signal);
  }
}

/** Raw serialized FontFaceAsset records carried by a persisted bundle map. */
export function persistedFontFaceRecords(bundle: {
  map?: Record<string, any>;
}): (PersistedFontFaceRecord & { uuid?: string })[] {
  return Object.values(bundle.map ?? {}).filter(
    (r): r is PersistedFontFaceRecord & { uuid?: string } =>
      r?.__type === "FontFaceAsset",
  );
}

/** Native font identity is a bounded nonempty id (mkShortId nanoid), never an
 * RFC UUID; a persisted record without one has no accountable identity. */
export const MAX_FONT_FACE_ASSET_ID_LENGTH = 128;

function persistedFontFaceUuid(rec: { uuid?: unknown }): string {
  const uuid = rec.uuid;
  if (
    typeof uuid !== "string" ||
    uuid.length === 0 ||
    uuid.length > MAX_FONT_FACE_ASSET_ID_LENGTH
  ) {
    throw new Error("FONT_IDENTITY_INVALID");
  }
  return uuid;
}

const FONT_IDENTITY_FIELDS = [
  "name",
  "family",
  "weight",
  "style",
  "stretch",
  "unicodeRange",
  "format",
  "data",
  "byteLength",
  "sha256",
] as const;

/**
 * Pre-mutation persisted-font boundary for incremental/partial bundle loads:
 * verifies each serialized FontFaceAsset record's canonical bytes, declared
 * container signature, and SHA-256 BEFORE the bundle is unbundled into a live
 * site, and enforces package bounds against the PROJECTED final owned
 * inventory under partial-bundle semantics: an incoming record replaces any
 * existing asset with the same uuid (so its new byteLength supersedes the old
 * one), repeated identical declarations of one uuid count once, and
 * conflicting duplicate identity definitions are rejected. Records duplicated
 * by package dependencies pass without double counting or double hashing.
 */
export async function validatePersistedFontFaceRecords(
  bundles: Iterable<{ map?: Record<string, any> }>,
  opts?: {
    site?: Site;
    signal?: AbortSignal;
    iidOf?: (asset: FontFaceAsset) => string | undefined;
    deletedIids?: Iterable<string>;
  },
): Promise<void> {
  const signal = opts?.signal;
  const stop = () => {
    if (signal?.aborted) throw new Error("FONT_CANCELED");
  };
  const seen = new Map<string, PersistedFontFaceRecord>();
  const bundleList = [...bundles];
  for (const bundle of bundleList) {
    for (const rec of persistedFontFaceRecords(bundle)) {
      stop();
      const uuid = persistedFontFaceUuid(rec);
      const prior = seen.get(uuid);
      if (prior) {
        if (
          FONT_IDENTITY_FIELDS.some(
            (field) => !isEqual(prior[field], rec[field]),
          )
        ) {
          throw new Error("FONT_IDENTITY_CONFLICT");
        }
        continue;
      }
      seen.set(uuid, rec);
      await validatePersistedFontFace(rec, signal);
    }
  }
  // Bounds apply to the FINAL native membership (array cardinality and bytes);
  // Site array replacement, deletedIids and fresh-uuid replacement all
  // supersede live assets. Chained bundles keep iids.
  let pending: InventoryEntry[] = (opts?.site?.fontFaceAssets ?? []).map(
    (a) => ({
      uuid: a.uuid,
      byteLength: a.byteLength,
      iid: opts?.iidOf?.(a),
      record: a,
    }),
  );
  const siteIid = opts?.site
    ? opts.iidOf?.(opts.site as unknown as FontFaceAsset)
    : undefined;
  for (const bundle of bundleList) {
    pending = projectPartialInventory(
      bundle as { root?: string; map?: Record<string, any> },
      "fontFaceAssets",
      "FontFaceAsset",
      pending,
      opts?.deletedIids,
      {
        invalid: "FONT_MEMBERSHIP_INVALID",
        tooLarge: "FONT_PACKAGE_TOO_LARGE",
        max: MAX_FONT_FACE_ASSETS,
      },
      siteIid,
    );
  }
  let total = 0;
  for (const e of pending) total += e.byteLength;
  if (
    pending.length > MAX_FONT_FACE_ASSETS ||
    total > MAX_FONT_FACE_PACKAGE_BYTES
  ) {
    throw new Error("FONT_PACKAGE_TOO_LARGE");
  }
  // Full-loader descriptor identity over the FINAL members (orphans excluded):
  // equal normalized descriptors must share the same bytes and format.
  const identities = new Map<string, PersistedFontFaceRecord>();
  for (const e of pending) {
    const rec = e.record as PersistedFontFaceRecord | undefined;
    if (!rec) continue;
    const key = fontFaceIdentityKey(rec);
    const prior = identities.get(key);
    if (
      prior &&
      (prior.sha256 !== rec.sha256 ||
        prior.data !== rec.data ||
        prior.format !== rec.format)
    ) {
      throw new Error("FONT_IDENTITY_CONFLICT");
    }
    identities.set(key, rec);
  }
}

/**
 * Pre-mutation boundary for fetched package bundles (root + every transitive
 * dependency) about to be unbundled into a LIVE bundler. Taggedunbundle reuses
 * cached instances by package id and overwrites their fields, so a tampered
 * package sharing an already-installed version would corrupt live dependency
 * assets before any post-unbundle check could throw. Each serialized package is
 * validated on its own (bytes, hash, identity, membership, bounds) first.
 */
export async function validatePersistedPackageBundles(
  packages: Iterable<{ model: { map?: Record<string, any> } }>,
  signal?: AbortSignal,
): Promise<void> {
  for (const pkg of packages) {
    await validatePersistedMediaRecords([pkg.model], { signal });
    await validatePersistedFontFaceRecords([pkg.model], { signal });
  }
}

/* ------------------------------------------------------------------ */
/* Shared owned-face coverage predicate (runtime + codegen)            */
/* ------------------------------------------------------------------ */

export interface FontFaceAssetLike {
  family: string;
  weight: string;
  style: string;
  stretch: string;
  unicodeRange: string;
}

/** Parse a font-weight descriptor ("normal" | "bold" | number | "lo hi") into
 * an inclusive numeric range. */
function weightRange(weight: string): [number, number] {
  const single = (w: string) =>
    w === "normal" ? 400 : w === "bold" ? 700 : parseInt(w, 10);
  const parts = weight.trim().split(/\s+/);
  if (parts.length === 2) {
    const [lo, hi] = [single(parts[0]), single(parts[1])];
    return [Math.min(lo, hi), Math.max(lo, hi)];
  }
  const v = single(parts[0]);
  return [v, v];
}

/** Inclusive percentage range of a font-stretch descriptor; "normal" is 100%. */
function stretchRange(stretch: string): [number, number] {
  const single = (s: string) => (s === "normal" ? 100 : parseInt(s, 10));
  const parts = stretch.trim().split(/\s+/);
  const lo = single(parts[0]);
  const hi = parts.length === 2 ? single(parts[1]) : lo;
  return [Math.min(lo, hi), Math.max(lo, hi)];
}

const MAX_CODE_POINT = 0x10ffff;

/** Whether the declared unicode-range (comma separated U+X, U+X-Y or U+4??
 * tokens, validated by the descriptor grammar) spans every code point. An
 * unparseable token is never treated as full coverage. */
function unicodeRangeIsFull(unicodeRange: string): boolean {
  const intervals: [number, number][] = [];
  for (const token of unicodeRange.split(",")) {
    const m = /^U\+([0-9A-F?]{1,6})(?:-([0-9A-F]{1,6}))?$/i.exec(token.trim());
    if (!m) return false;
    if (m[1].includes("?")) {
      if (m[2]) return false;
      intervals.push([
        parseInt(m[1].replace(/\?/g, "0"), 16),
        parseInt(m[1].replace(/\?/g, "F"), 16),
      ]);
    } else {
      const lo = parseInt(m[1], 16);
      intervals.push([lo, m[2] ? parseInt(m[2], 16) : lo]);
    }
  }
  intervals.sort((x, y) => x[0] - y[0]);
  let next = 0;
  for (const [lo, hi] of intervals) {
    if (lo > next) return false;
    next = Math.max(next, hi + 1);
  }
  return next > MAX_CODE_POINT;
}

/**
 * Whether an owned face supplies glyphs for the used variant: same family
 * (case-insensitive), compatible style and a weight range containing the used
 * weight. Such a face is emitted in generated output even when it carries only
 * part of the repertoire, so its declared unicode-range/stretch keeps applying.
 */
export function fontFaceSuppliesVariant(
  face: Pick<FontFaceAssetLike, "family" | "weight" | "style">,
  fontFamily: string,
  italic: boolean,
  weight: number,
): boolean {
  if (face.family.toLowerCase() !== fontFamily.toLowerCase()) return false;
  const faceItalic = face.style === "italic" || face.style === "oblique";
  if (italic !== faceItalic) return false;
  const [lo, hi] = weightRange(face.weight);
  return weight >= lo && weight <= hi;
}

/**
 * The one owned-face coverage predicate shared by Studio runtime and codegen.
 * A used variant is owned-covered — and may stop requesting its external
 * source — only when the face supplies it AND declares the whole code-point
 * repertoire AND a font-stretch containing the normal (100%) width actually
 * used. A partially-ranged or non-normal-stretch face is never self-contained:
 * code points or widths it does not declare resolve through the existing
 * external/local font path (google-font import link or system resolution).
 */
export function fontFaceCoversVariant(
  face: FontFaceAssetLike,
  fontFamily: string,
  italic: boolean,
  weight: number,
): boolean {
  if (!fontFaceSuppliesVariant(face, fontFamily, italic, weight)) return false;
  const [lo, hi] = stretchRange(face.stretch);
  return lo <= 100 && 100 <= hi && unicodeRangeIsFull(face.unicodeRange);
}

/** Lowercased family names that have at least one owned face. Used by the
 * Studio font picker/runtime so owned families are treated as available. */
export function ownedFontFaceFamilies(
  sites: Iterable<Pick<Site, "fontFaceAssets">>,
): Set<string> {
  const families = new Set<string>();
  for (const site of sites) {
    for (const face of site.fontFaceAssets ?? []) {
      families.add(face.family.toLowerCase());
    }
  }
  return families;
}

/**
 * Split font usage into owned faces and the residual that still needs the
 * external font path. `referencedFaces` are the owned faces that supply any
 * used variant (deduped by face identity, authored order) and are emitted with
 * their declared descriptors; `uncovered` keeps every variant that no
 * FULLY covering owned face (see fontFaceCoversVariant) provides, so
 * partially-ranged / non-normal-stretch faces never narrow the external
 * request. Sharing this one predicate keeps runtime and codegen consistent.
 */
export function splitFontUsageCoverage<
  U extends {
    fontFamily: string;
    variants: { italic: boolean; weight: number }[];
  },
>(
  site: Pick<Site, "fontFaceAssets">,
  usages: U[],
): { referencedFaces: FontFaceAsset[]; uncovered: U[] } {
  const faces = site.fontFaceAssets ?? [];
  const used = new Set<FontFaceAsset>();
  const uncovered: U[] = [];
  for (const usage of usages) {
    const missing = usage.variants.filter(
      (v) =>
        !faces.some((f) =>
          fontFaceCoversVariant(f, usage.fontFamily, v.italic, v.weight),
        ),
    );
    for (const v of usage.variants) {
      for (const f of faces) {
        if (fontFaceSuppliesVariant(f, usage.fontFamily, v.italic, v.weight)) {
          used.add(f);
        }
      }
    }
    if (missing.length) {
      uncovered.push({ ...usage, variants: missing });
    }
  }
  return {
    referencedFaces: [...faces.filter((f) => used.has(f))],
    uncovered,
  };
}

/** @font-face CSS for owned faces, reusing the print font codec emitter so
 * runtime and print/codegen output share one byte format. */
export function ownedFontFacesCss(faces: FontFaceAsset[]): string {
  if (!faces.length) return "";
  const resources: PrintFontResource[] = faces.map((f) => ({
    family: f.family,
    weight: f.weight,
    style: f.style,
    stretch: f.stretch,
    unicodeRange: f.unicodeRange,
    format: f.format,
    data: f.data,
    sha256: f.sha256,
  }));
  return printFontsCss(normalizePrintFonts(resources));
}
